// AI Usage — oversight dashboard aggregating every MCP tool call across
// all connected assistants: total volume, calls-per-day trend, the most
// used tools, and a recent-activity feed. Data: GET /admin/ai/usage
// (platform-wide, AI_USAGE_VIEW).
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { fmtRelative } from "@/lib/format";

interface ToolCount {
  tool: string;
  count: number;
}
interface DayCount {
  day: string;
  count: number;
}
interface AuditEvent {
  tool: string;
  outcome: string;
  is_error: boolean;
  admin_email: string;
  client_id: string;
  created_at: string;
}
interface UsageReport {
  since: string;
  by_tool: ToolCount[] | null;
  by_day: DayCount[] | null;
  recent: AuditEvent[] | null;
  total: number;
}

export const Route = createFileRoute("/_app/ai/usage")({
  component: UsagePage,
});

function outcomeTone(o: string, isErr: boolean): "success" | "warning" | "danger" | "muted" {
  if (isErr || o === "error") return "danger";
  if (o === "blocked" || o === "denied") return "danger";
  if (o === "confirmation_required" || o === "pending_approval") return "warning";
  if (o === "executed" || o === "ok") return "success";
  return "muted";
}

function UsagePage() {
  const [days, setDays] = useState(30);
  const q = useQuery({
    queryKey: ["ai", "usage", days],
    queryFn: () => api<UsageReport>(`/admin/ai/usage?days=${days}`),
    refetchInterval: 60_000,
  });

  const byTool = q.data?.by_tool ?? [];
  const byDay = q.data?.by_day ?? [];
  const recent = q.data?.recent ?? [];
  const total = q.data?.total ?? 0;
  const maxTool = byTool.reduce((m, t) => Math.max(m, t.count), 0) || 1;
  const maxDay = byDay.reduce((m, d) => Math.max(m, d.count), 0) || 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-t-1">AI Usage</h1>
          <p className="text-sm text-muted">
            Every tool call connected AI assistants have made against the admin tools.
          </p>
        </div>
        <SegmentedControl
          value={String(days)}
          onChange={(v) => setDays(Number(v))}
          options={[
            { value: "7", label: "7d" },
            { value: "30", label: "30d" },
            { value: "90", label: "90d" },
          ]}
        />
      </div>

      {q.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <>
          <Card>
            <CardBody>
              <div className="text-xs uppercase tracking-wide text-t-3">Total tool calls</div>
              <div className="text-3xl font-semibold text-t-1">{total.toLocaleString()}</div>
              <div className="text-xs text-muted">in the last {days} days</div>
            </CardBody>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Calls per day</CardTitle>
              </CardHeader>
              <CardBody>
                {byDay.length === 0 ? (
                  <EmptyState title="No activity yet" />
                ) : (
                  <div className="flex h-40 items-end gap-1">
                    {byDay.map((d) => (
                      <div key={d.day} className="flex flex-1 flex-col items-center gap-1">
                        <div
                          className="w-full rounded-t bg-accent/70"
                          style={{ height: `${(d.count / maxDay) * 100}%` }}
                          title={`${d.day}: ${d.count}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top tools</CardTitle>
              </CardHeader>
              <CardBody>
                {byTool.length === 0 ? (
                  <EmptyState title="No activity yet" />
                ) : (
                  <div className="space-y-2">
                    {byTool.slice(0, 10).map((t) => (
                      <div key={t.tool} className="flex items-center gap-3">
                        <div className="w-40 shrink-0 truncate font-mono text-xs text-t-2">
                          {t.tool}
                        </div>
                        <div className="h-4 flex-1 rounded bg-surface-2">
                          <div
                            className="h-4 rounded bg-accent/70"
                            style={{ width: `${(t.count / maxTool) * 100}%` }}
                          />
                        </div>
                        <div className="w-10 shrink-0 text-right text-xs text-t-2">
                          {t.count}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Recent activity</CardTitle>
            </CardHeader>
            <CardBody>
              {recent.length === 0 ? (
                <EmptyState title="No recent tool calls" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-t-3">
                        <th className="py-2 pr-4">Tool</th>
                        <th className="py-2 pr-4">Outcome</th>
                        <th className="py-2 pr-4">Admin</th>
                        <th className="py-2 pr-4">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((e, i) => (
                        <tr key={i} className="border-t border-line">
                          <td className="py-2 pr-4 font-mono text-xs text-t-1">{e.tool}</td>
                          <td className="py-2 pr-4">
                            <Badge tone={outcomeTone(e.outcome, e.is_error)}>{e.outcome}</Badge>
                          </td>
                          <td className="py-2 pr-4 text-t-2">{e.admin_email || "—"}</td>
                          <td className="py-2 pr-4 text-t-2">{fmtRelative(e.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
